import React, { useMemo } from 'react'

// ── IMS rounded-rectangle geometry ────────────────────────────────────────
// Corner centers: NW=(180,160), NE=(580,160), SE=(580,260), SW=(180,260)
// Horizontal span 400, vertical span 100, outer r=130, inner r=75, CL r=102.5
const R_OUT = 130
const R_IN  = 75
const R_CL  = 102.5

const OUTER_PATH =
  'M 180,30 L 580,30 A 130,130 0 0 1 710,160 L 710,260 ' +
  'A 130,130 0 0 1 580,390 L 180,390 A 130,130 0 0 1 50,260 ' +
  'L 50,160 A 130,130 0 0 1 180,30 Z'

const INNER_PATH =
  'M 180,85 L 580,85 A 75,75 0 0 1 655,160 L 655,260 ' +
  'A 75,75 0 0 1 580,335 L 180,335 A 75,75 0 0 1 105,260 ' +
  'L 105,160 A 75,75 0 0 1 180,85 Z'

// ── Centerline position (t=0 at S/F, CCW = race direction) ────────────────
function xyAt(t: number): [number, number] {
  const r   = R_CL
  const arc = (Math.PI / 2) * r                          // ~161 px per corner
  const P   = 2 * 400 + 2 * 100 + 4 * arc               // total perimeter

  let d = (((t % 1) + 1) % 1) * P

  type Seg = { len: number; fn: (s: number) => [number, number] }
  const segs: Seg[] = [
    { len: 200, fn: s => [380 - s, 260 + r] },                              // bottom-left
    { len: arc, fn: s => { const a = Math.PI/2 + (s/arc)*(Math.PI/2);      // SW corner
        return [180 + r*Math.cos(a), 260 + r*Math.sin(a)] } },
    { len: 100, fn: s => [180 - r, 260 - s] },                              // left straight
    { len: arc, fn: s => { const a = Math.PI + (s/arc)*(Math.PI/2);        // NW corner
        return [180 + r*Math.cos(a), 160 + r*Math.sin(a)] } },
    { len: 400, fn: s => [180 + s, 160 - r] },                              // top straight
    { len: arc, fn: s => { const a = 1.5*Math.PI + (s/arc)*(Math.PI/2);   // NE corner
        return [580 + r*Math.cos(a), 160 + r*Math.sin(a)] } },
    { len: 100, fn: s => [580 + r, 160 + s] },                              // right straight
    { len: arc, fn: s => { const a = (s/arc)*(Math.PI/2);                  // SE corner
        return [580 + r*Math.cos(a), 260 + r*Math.sin(a)] } },
    { len: 200, fn: s => [580 - s, 260 + r] },                              // bottom-right
  ]

  for (const seg of segs) {
    if (d <= seg.len) return seg.fn(d)
    d -= seg.len
  }
  return [380, 260 + r]
}

// ── Section → t mapping ────────────────────────────────────────────────────
const SECTION_T: [string, number][] = [
  ['Start/Finish',   0.00],
  ['Pit Lane',       0.02],
  ['Front Straight', 0.06],
  ['Turn 1',         0.16],
  ['Short Chute 1',  0.26],
  ['Turn 2',         0.34],
  ['Back Straight',  0.50],
  ['Turn 3',         0.66],
  ['Short Chute 2',  0.74],
  ['Turn 4',         0.84],
]

function locationToT(loc: string, carNum: number): number {
  const match = SECTION_T.find(([k]) => loc.toLowerCase().includes(k.toLowerCase()))
  const base  = match ? match[1] : (carNum * 0.031) % 1
  return (base + (carNum % 11) * 0.006) % 1
}

// ── Public types ───────────────────────────────────────────────────────────
export interface CarOnTrack {
  driverNumber:  number
  driverName:    string
  location:      string
  position:      number
  isDNF:         boolean
  isHighlighted: boolean
  color:         string
}

interface Props { cars: CarOnTrack[] }

// ── Component ──────────────────────────────────────────────────────────────
export default function TrackMap({ cars }: Props) {
  const positioned = useMemo(() =>
    cars.map(c => { const [x, y] = xyAt(locationToT(c.location, c.driverNumber)); return { ...c, x, y } }),
  [cars])

  const medal = ['#d4a017', '#9ca3af', '#cd7f32']

  return (
    <svg viewBox="0 0 760 420" style={{ width: '100%', display: 'block' }}>
      {/* Background */}
      <rect width="760" height="420" fill="#06091a" rx="10"/>

      {/* Track surface */}
      <path d={`${OUTER_PATH} ${INNER_PATH}`} fill="#171d30" fillRule="evenodd"/>

      {/* Walls */}
      <path d={OUTER_PATH} fill="none" stroke="#2e3450" strokeWidth="1.5"/>
      <path d={INNER_PATH} fill="#0d1120" stroke="#2e3450" strokeWidth="1.5"/>

      {/* Infield text */}
      <text x="380" y="204" textAnchor="middle" fill="rgba(255,255,255,0.05)"
        fontSize="40" fontWeight="900" fontFamily="Montserrat,sans-serif">IMS</text>
      <text x="380" y="226" textAnchor="middle" fill="rgba(255,255,255,0.03)"
        fontSize="11" letterSpacing="5" fontFamily="Montserrat,sans-serif">INDIANAPOLIS</text>

      {/* S/F line */}
      <line x1="380" y1="337" x2="380" y2="388" stroke="#c81026" strokeWidth="3" opacity="0.85"/>
      <text x="388" y="378" fill="#c81026" fontSize="8"
        fontFamily="Montserrat,sans-serif" fontWeight="700" opacity="0.7">S/F</text>

      {/* Section labels */}
      {[
        { x: 380, y: 414, t: 'middle', text: 'FRONT STRAIGHT' },
        { x: 380, y: 11,  t: 'middle', text: 'BACK STRAIGHT'  },
        { x: 54,  y: 334, t: 'middle', text: 'TURN 1'         },
        { x: 54,  y: 86,  t: 'middle', text: 'TURN 2'         },
        { x: 706, y: 86,  t: 'middle', text: 'TURN 3'         },
        { x: 706, y: 334, t: 'middle', text: 'TURN 4'         },
      ].map(l => (
        <text key={l.text} x={l.x} y={l.y} textAnchor={l.t as any}
          fill="rgba(255,255,255,0.2)" fontSize="8.5" letterSpacing="0.8"
          fontFamily="Montserrat,sans-serif" fontWeight="700">{l.text}</text>
      ))}

      {/* Dim non-highlighted, non-DNF cars */}
      {positioned.filter(c => !c.isHighlighted && !c.isDNF).map(c => (
        <circle key={c.driverNumber} cx={c.x} cy={c.y} r={4}
          fill="rgba(255,255,255,0.17)" stroke="rgba(255,255,255,0.07)" strokeWidth="0.8"/>
      ))}

      {/* Dim DNF cars */}
      {positioned.filter(c => !c.isHighlighted && c.isDNF).map(c => (
        <circle key={c.driverNumber} cx={c.x} cy={c.y} r={3}
          fill="rgba(200,16,38,0.2)" stroke="rgba(200,16,38,0.1)" strokeWidth="0.8"/>
      ))}

      {/* Highlighted drivers */}
      {positioned.filter(c => c.isHighlighted && !c.isDNF).map(c => {
        const isTop3 = c.position <= 3
        return (
          <g key={c.driverNumber}>
            <circle cx={c.x} cy={c.y} r={13} fill={c.color} opacity={0.18}/>
            <circle cx={c.x} cy={c.y} r={9}  fill={c.color} stroke="#fff" strokeWidth="1.5"/>
            <text x={c.x} y={c.y} textAnchor="middle" dominantBaseline="middle"
              fill="#fff" fontSize="7" fontWeight="800" fontFamily="Montserrat,sans-serif">
              {c.driverNumber}
            </text>
            {isTop3 && (
              <>
                <circle cx={c.x+10} cy={c.y-10} r={6} fill={medal[c.position-1]} stroke="#fff" strokeWidth="1"/>
                <text x={c.x+10} y={c.y-10} textAnchor="middle" dominantBaseline="middle"
                  fill="#fff" fontSize="5.5" fontWeight="900" fontFamily="Montserrat,sans-serif">
                  {c.position}
                </text>
              </>
            )}
          </g>
        )
      })}

      {/* Highlighted DNF drivers */}
      {positioned.filter(c => c.isHighlighted && c.isDNF).map(c => (
        <g key={c.driverNumber}>
          <circle cx={c.x} cy={c.y} r={8} fill="rgba(200,16,38,0.5)" stroke="#c81026" strokeWidth="1.5"/>
          <text x={c.x} y={c.y} textAnchor="middle" dominantBaseline="middle"
            fill="#fff" fontSize="6.5" fontWeight="800" fontFamily="Montserrat,sans-serif">✕</text>
        </g>
      ))}
    </svg>
  )
}
